import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Filter,
  Plus,
  Upload,
  Search,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Trash2,
} from 'lucide-react';
import { Pagination } from '@/components/shared/Pagination';
import {
  useRegrasICMS,
  useRegrasIBSCBS,
  useImportarRegrasICMS,
  useImportarRegrasIBSCBS,
  useExcluirRegraICMS,
  useExcluirRegraIBSCBS,
} from '@/services/Configuracoes';
import type { RegraDeParaICMSApi, RegraDeParaIBSCBSApi } from '@/services/Configuracoes';

interface TabelaDeParaProps {
  tipo: 'ibs-cbs' | 'icms';
}

type RegraRow = {
  id: number;
  cfop: string;
  descricao: string;
  cstOrigem: string;
  destinacao: string;
  cfopPara: string;
  cstPara: string;
  credito1: string;
  credito2: string;
  codContIntegracao: string;
  codContAux1: string;
  usuarioManutencao: string;
};

interface ColumnCheck {
  apiField: string;
  label: string;
  excelHeadersAceitos: string[];
  foundHeader: string | null;
  required: boolean;
}

const ICMS_COLUMN_MAP: Omit<ColumnCheck, 'foundHeader'>[] = [
  {
    apiField: 'CFOP',
    label: 'CFOP',
    excelHeadersAceitos: ['CFOP', 'CFOPOriginal'],
    required: true,
  },
  {
    apiField: 'Descricao',
    label: 'Descricao',
    excelHeadersAceitos: ['Descricao', 'Descrição'],
    required: true,
  },
  {
    apiField: 'CSTOrigem',
    label: 'CST origem',
    excelHeadersAceitos: ['CST origem', 'CSTOrigem', 'CST Origem'],
    required: true,
  },
  {
    apiField: 'Destinacao',
    label: 'Destinação',
    excelHeadersAceitos: ['Destinação', 'Destinacao'],
    required: true,
  },
  { apiField: 'CFOPPara', label: 'CFOPPara', excelHeadersAceitos: ['CFOPPara'], required: true },
  { apiField: 'CSTPara', label: 'CSTPara', excelHeadersAceitos: ['CSTPara'], required: true },
  {
    apiField: 'RF_TomarCreditoICMS',
    label: 'RF_TomarCreditoICMS',
    excelHeadersAceitos: ['RF_TomarCreditoICMS'],
    required: true,
  },
  {
    apiField: 'RF_TomarCreditoICMSST',
    label: 'RF_TomarCreditoICMSST',
    excelHeadersAceitos: ['RF_TomarCreditoICMSST'],
    required: true,
  },
  {
    apiField: 'CodContIntegracao',
    label: 'CodContIntegracao',
    excelHeadersAceitos: ['CodContIntegracao'],
    required: false,
  },
  {
    apiField: 'CodContAux1',
    label: 'CodContAux1',
    excelHeadersAceitos: ['CodContAux1'],
    required: false,
  },
  {
    apiField: 'UsuarioManutencao',
    label: 'UsuarioManutencao',
    excelHeadersAceitos: ['UsuarioManutencao'],
    required: false,
  },
];

const IBSCBS_COLUMN_MAP: Omit<ColumnCheck, 'foundHeader'>[] = [
  { apiField: 'CFOP', label: 'CFOP', excelHeadersAceitos: ['CFOP'], required: true },
  {
    apiField: 'Descricao',
    label: 'Descricao',
    excelHeadersAceitos: ['Descricao', 'Descrição'],
    required: true,
  },
  {
    apiField: 'CSTOrigem',
    label: 'CST origem',
    excelHeadersAceitos: ['CST origem', 'CSTOrigem', 'CST Origem'],
    required: true,
  },
  {
    apiField: 'Destinacao',
    label: 'Destinação',
    excelHeadersAceitos: ['Destinação', 'Destinacao'],
    required: true,
  },
  { apiField: 'CFOPPara', label: 'CFOPPara', excelHeadersAceitos: ['CFOPPara'], required: true },
  { apiField: 'CSTPara', label: 'CSTPara', excelHeadersAceitos: ['CSTPara'], required: true },
  {
    apiField: 'RF_TomarCreditoIBS',
    label: 'RF_TomarCreditoIBS',
    excelHeadersAceitos: ['RF_TomarCreditoIBS', 'RF_TomarCreditoICMS'],
    required: true,
  },
  {
    apiField: 'RF_TomarCreditoCBS',
    label: 'RF_TomarCreditoCBS',
    excelHeadersAceitos: ['RF_TomarCreditoCBS', 'RF_TomarCreditoICMSST'],
    required: true,
  },
  {
    apiField: 'CodContIntegracao',
    label: 'CodContIntegracao',
    excelHeadersAceitos: ['CodContIntegracao'],
    required: false,
  },
  {
    apiField: 'CodContAux1',
    label: 'CodContAux1',
    excelHeadersAceitos: ['CodContAux1'],
    required: false,
  },
  {
    apiField: 'UsuarioManutencao',
    label: 'UsuarioManutencao',
    excelHeadersAceitos: ['UsuarioManutencao'],
    required: false,
  },
];

function buildColumnChecks(
  excelHeaders: string[],
  map: Omit<ColumnCheck, 'foundHeader'>[]
): ColumnCheck[] {
  return map.map(col => ({
    ...col,
    foundHeader: col.excelHeadersAceitos.find(h => excelHeaders.includes(h)) ?? null,
  }));
}

function getExcelHeaders(sheet: XLSX.WorkSheet): string[] {
  const raw = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
  if (!raw.length) return [];
  return (raw[0] as string[]).map(h => String(h ?? '').trim());
}

interface ParseResult<T> {
  regras: T[];
  columnChecks: ColumnCheck[];
  missingRequired: string[];
}

function parseExcelICMS(sheet: XLSX.WorkSheet): ParseResult<Partial<RegraDeParaICMSApi>> {
  const headers = getExcelHeaders(sheet);
  const columnChecks = buildColumnChecks(headers, ICMS_COLUMN_MAP);
  const missingRequired = columnChecks
    .filter(c => c.required && !c.foundHeader)
    .map(c => c.excelHeadersAceitos[0]);

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const regras = rows
    .filter(row => row['CFOP'] || row['CFOPPara'])
    .map(row => ({
      CFOP: String(row['CFOP'] ?? row['CFOPOriginal'] ?? '').trim(),
      Descricao: String(row['Descricao'] ?? row['Descrição'] ?? '').trim(),
      CSTOrigem: String(row['CST origem'] ?? row['CSTOrigem'] ?? row['CST Origem'] ?? '').trim(),
      Destinacao: String(row['Destinação'] ?? row['Destinacao'] ?? '').trim(),
      CFOPPara: String(row['CFOPPara'] ?? '').trim(),
      CSTPara: String(row['CSTPara'] ?? '').trim(),
      RF_TomarCreditoICMS:
        String(row['RF_TomarCreditoICMS'] ?? 'N')
          .trim()
          .toUpperCase() === 'S'
          ? 'S'
          : 'N',
      RF_TomarCreditoICMSST:
        String(row['RF_TomarCreditoICMSST'] ?? 'N')
          .trim()
          .toUpperCase() === 'S'
          ? 'S'
          : 'N',
      CodContIntegracao: String(row['CodContIntegracao'] ?? '').trim(),
      CodContAux1: String(row['CodContAux1'] ?? '').trim(),
      UsuarioManutencao: String(row['UsuarioManutencao'] ?? '').trim(),
    }))
    .filter(r => r.CFOP || r.CFOPPara);

  return { regras, columnChecks, missingRequired };
}

function parseExcelIBSCBS(sheet: XLSX.WorkSheet): ParseResult<Partial<RegraDeParaIBSCBSApi>> {
  const headers = getExcelHeaders(sheet);
  const columnChecks = buildColumnChecks(headers, IBSCBS_COLUMN_MAP);
  const missingRequired = columnChecks
    .filter(c => c.required && !c.foundHeader)
    .map(c => c.excelHeadersAceitos[0]);

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const regras = rows
    .filter(row => row['CFOP'] || row['CFOPPara'])
    .map(row => ({
      CFOP: String(row['CFOP'] ?? '').trim(),
      Descricao: String(row['Descricao'] ?? row['Descrição'] ?? '').trim(),
      CSTOrigem: String(row['CST origem'] ?? row['CSTOrigem'] ?? '').trim(),
      Destinacao: String(row['Destinação'] ?? row['Destinacao'] ?? '').trim(),
      CFOPPara: String(row['CFOPPara'] ?? '').trim(),
      CSTPara: String(row['CSTPara'] ?? '').trim(),
      RF_TomarCreditoIBS:
        String(row['RF_TomarCreditoIBS'] ?? row['RF_TomarCreditoICMS'] ?? 'N')
          .trim()
          .toUpperCase() === 'S'
          ? 'S'
          : 'N',
      RF_TomarCreditoCBS:
        String(row['RF_TomarCreditoCBS'] ?? row['RF_TomarCreditoICMSST'] ?? 'N')
          .trim()
          .toUpperCase() === 'S'
          ? 'S'
          : 'N',
      CodContIntegracao: String(row['CodContIntegracao'] ?? '').trim(),
      CodContAux1: String(row['CodContAux1'] ?? '').trim(),
      UsuarioManutencao: String(row['UsuarioManutencao'] ?? '').trim(),
    }))
    .filter(r => r.CFOP || r.CFOPPara);

  return { regras, columnChecks, missingRequired };
}

function toRow(item: RegraDeParaICMSApi): RegraRow {
  return {
    id: item.ID,
    cfop: item.CFOP,
    descricao: item.Descricao,
    cstOrigem: item.CSTOrigem,
    destinacao: item.Destinacao,
    cfopPara: item.CFOPPara,
    cstPara: item.CSTPara,
    credito1: item.RF_TomarCreditoICMS,
    credito2: item.RF_TomarCreditoICMSST,
    codContIntegracao: item.CodContIntegracao,
    codContAux1: item.CodContAux1,
    usuarioManutencao: item.UsuarioManutencao,
  };
}

function toRowIBS(item: RegraDeParaIBSCBSApi): RegraRow {
  return {
    id: item.ID,
    cfop: item.CFOP,
    descricao: item.Descricao,
    cstOrigem: item.CSTOrigem,
    destinacao: item.Destinacao,
    cfopPara: item.CFOPPara,
    cstPara: item.CSTPara,
    credito1: item.RF_TomarCreditoIBS,
    credito2: item.RF_TomarCreditoCBS,
    codContIntegracao: item.CodContIntegracao,
    codContAux1: item.CodContAux1,
    usuarioManutencao: item.UsuarioManutencao,
  };
}

export function TabelaDePara({ tipo }: TabelaDeParaProps) {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(20);

  type ImportState = {
    regras: Partial<RegraDeParaICMSApi>[] | Partial<RegraDeParaIBSCBSApi>[];
    columnChecks: ColumnCheck[];
    missingRequired: string[];
    fileName: string;
  };
  const [importState, setImportState] = useState<ImportState | null>(null);
  const [importError, setImportError] = useState('');
  const [regraParaExcluir, setRegraParaExcluir] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const icmsQuery = useRegrasICMS();
  const ibsCbsQuery = useRegrasIBSCBS();
  const importarICMS = useImportarRegrasICMS();
  const importarIBSCBS = useImportarRegrasIBSCBS();
  const importMutation = tipo === 'icms' ? importarICMS : importarIBSCBS;
  const excluirICMS = useExcluirRegraICMS();
  const excluirIBSCBS = useExcluirRegraIBSCBS();
  const excluirMutation = tipo === 'icms' ? excluirICMS : excluirIBSCBS;

  const isLoading = tipo === 'icms' ? icmsQuery.isLoading : ibsCbsQuery.isLoading;

  const linhas: RegraRow[] = useMemo(() => {
    if (tipo === 'icms') return (icmsQuery.data?.data ?? []).map(toRow);
    return (ibsCbsQuery.data?.data ?? []).map(toRowIBS);
  }, [tipo, icmsQuery.data, ibsCbsQuery.data]);

  const dadosFiltrados = useMemo(() => {
    if (!busca.trim()) return linhas;
    const termo = busca.toLowerCase();
    return linhas.filter(
      item =>
        item.cfop.includes(termo) ||
        item.descricao.toLowerCase().includes(termo) ||
        item.cstOrigem.includes(termo) ||
        item.destinacao.toLowerCase().includes(termo)
    );
  }, [busca, linhas]);

  const totalPaginas = Math.max(1, Math.ceil(dadosFiltrados.length / itensPorPagina));
  const inicio = (pagina - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicio, inicio + itensPorPagina);

  const handleBuscaChange = (valor: string) => {
    setBusca(valor);
    setPagina(1);
  };
  const handleItensPorPaginaChange = (itens: number) => {
    setItensPorPagina(itens);
    setPagina(1);
  };

  const handleImportarClick = () => {
    setImportError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = event => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const result = tipo === 'icms' ? parseExcelICMS(sheet) : parseExcelIBSCBS(sheet);

        if (result.regras.length === 0) {
          setImportError(
            'Nenhuma linha válida encontrada. Verifique se o arquivo está no formato correto.'
          );
          return;
        }

        setImportState({
          regras: result.regras as ImportState['regras'],
          columnChecks: result.columnChecks,
          missingRequired: result.missingRequired,
          fileName: file.name,
        });
      } catch {
        setImportError(
          'Não foi possível ler o arquivo. Verifique se é um Excel válido (.xlsx ou .xls).'
        );
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleConfirmarImport = () => {
    if (!importState) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    importMutation.mutate(importState.regras as any, {
      onSuccess: () => setImportState(null),
    });
  };

  const handleConfirmarExclusao = () => {
    if (regraParaExcluir == null) return;
    excluirMutation.mutate(regraParaExcluir, {
      onSuccess: () => setRegraParaExcluir(null),
    });
  };

  const credito1Label = tipo === 'icms' ? 'Créd. ICMS' : 'Créd. IBS';
  const credito2Label = tipo === 'icms' ? 'Créd. ICMS ST' : 'Créd. CBS';

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const previewRows = (importState?.regras ?? []) as Array<Record<string, string>>;

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por CFOP, descrição, CST..."
            value={busca}
            onChange={e => handleBuscaChange(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 h-9">
          <Filter className="h-3.5 w-3.5" />
          Filtros
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="gap-1.5 h-9">
          <Plus className="h-3.5 w-3.5" />
          Adicionar Regra...
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={handleImportarClick}>
          <Upload className="h-3.5 w-3.5" />
          Importar Arquivo...
        </Button>
      </div>

      {importError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{importError}</span>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {dadosFiltrados.length} registro{dadosFiltrados.length !== 1 ? 's' : ''} encontrado
        {dadosFiltrados.length !== 1 ? 's' : ''}
      </p>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-20">CFOP</TableHead>
              <TableHead className="min-w-[180px]">Descrição</TableHead>
              <TableHead className="w-24">CST Origem</TableHead>
              <TableHead className="w-32">Destinação</TableHead>
              <TableHead className="w-24">CFOP Para</TableHead>
              <TableHead className="w-24">CST Para</TableHead>
              <TableHead className="w-24 text-center">{credito1Label}</TableHead>
              <TableHead className="w-24 text-center">{credito2Label}</TableHead>
              <TableHead className="w-40">Cód. Cont. Integ.</TableHead>
              <TableHead className="w-32">Cód. Cont. Aux1</TableHead>
              <TableHead className="min-w-[120px]">Usuário Manut.</TableHead>
              <TableHead className="w-16 text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dadosPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8 text-muted-foreground text-sm">
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            ) : (
              dadosPaginados.map(item => (
                <TableRow key={item.id} className="hover:bg-muted/30">
                  <TableCell className="font-mono text-sm font-medium">{item.cfop}</TableCell>
                  <TableCell className="text-sm">{item.descricao}</TableCell>
                  <TableCell className="font-mono text-sm">{item.cstOrigem}</TableCell>
                  <TableCell className="text-sm">{item.destinacao}</TableCell>
                  <TableCell className="font-mono text-sm">{item.cfopPara}</TableCell>
                  <TableCell className="font-mono text-sm">{item.cstPara}</TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className={
                        item.credito1 === 'S'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-600 border-red-200'
                      }
                    >
                      {item.credito1}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className={
                        item.credito2 === 'S'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-600 border-red-200'
                      }
                    >
                      {item.credito2}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.codContIntegracao || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.codContAux1 || '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {item.usuarioManutencao}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setRegraParaExcluir(item.id)}
                      aria-label="Excluir regra"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!importState} onOpenChange={open => !open && setImportState(null)}>
        <DialogContent className="w-[90vw] max-w-none sm:max-w-none max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>Confirmar Importação</DialogTitle>
            <DialogDescription>
              Arquivo: <span className="font-medium">{importState?.fileName}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div className="flex items-center gap-4 rounded-md bg-muted/50 px-4 py-3">
              <span className="text-3xl font-bold text-foreground">
                {importState?.regras.length ?? 0}
              </span>
              <div>
                <p className="text-sm font-medium">regras encontradas no arquivo</p>
                <p className="text-xs text-muted-foreground">
                  As regras serão adicionadas ao banco. Duplicatas (mesmo CFOP + CST origem) devem
                  ser verificadas antes.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                Compatibilidade de Colunas
              </p>

              {(importState?.missingRequired?.length ?? 0) > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Colunas obrigatórias não encontradas:{' '}
                    <strong>{importState?.missingRequired.join(', ')}</strong>. Renomeie as colunas
                    no Excel e tente novamente.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {importState?.columnChecks.map(col => (
                  <div
                    key={col.apiField}
                    className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs border ${
                      col.foundHeader
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : col.required
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-muted/40 border-border text-muted-foreground'
                    }`}
                  >
                    {col.foundHeader ? (
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
                    ) : col.required ? (
                      <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
                    ) : (
                      <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-40" />
                    )}
                    <span className="font-medium truncate">{col.label}</span>
                    {col.foundHeader && col.foundHeader !== col.excelHeadersAceitos[0] && (
                      <span className="ml-auto opacity-60 shrink-0">→ {col.foundHeader}</span>
                    )}
                    {!col.foundHeader && !col.required && (
                      <span className="ml-auto opacity-50 shrink-0">opcional</span>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                Colunas ignoradas (não enviadas para a API): DataHoraManutencao, ID, FlagSalvar,
                CFOPOriginal, CSTOriginal, Legado.
              </p>
            </div>

            {previewRows.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  Prévia das primeiras {Math.min(previewRows.length, 5)} linhas
                </p>
                <ScrollArea className="rounded-md border w-full">
                  <div className="min-w-max">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/60 border-b">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            CFOP
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            Descrição
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            CST Origem
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            Destinação
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            CFOP Para
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            CST Para
                          </th>
                          <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                            {credito1Label}
                          </th>
                          <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                            {credito2Label}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            Cód. Cont. Integ.
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            Cód. Cont. Aux1
                          </th>
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                            Usuário Manut.
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.slice(0, 5).map((r, i) => {
                          const c1 =
                            tipo === 'icms' ? r['RF_TomarCreditoICMS'] : r['RF_TomarCreditoIBS'];
                          const c2 =
                            tipo === 'icms' ? r['RF_TomarCreditoICMSST'] : r['RF_TomarCreditoCBS'];
                          return (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/20'}>
                              <td className="px-3 py-1.5 font-mono font-medium">{r['CFOP']}</td>
                              <td className="px-3 py-1.5 max-w-[180px] truncate">
                                {r['Descricao']}
                              </td>
                              <td className="px-3 py-1.5 font-mono">{r['CSTOrigem']}</td>
                              <td className="px-3 py-1.5">{r['Destinacao']}</td>
                              <td className="px-3 py-1.5 font-mono">{r['CFOPPara']}</td>
                              <td className="px-3 py-1.5 font-mono">{r['CSTPara']}</td>
                              <td className="px-3 py-1.5 text-center">
                                <span
                                  className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 font-semibold ${c1 === 'S' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}
                                >
                                  {c1}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                <span
                                  className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 font-semibold ${c2 === 'S' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}
                                >
                                  {c2}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {r['CodContIntegracao'] || '—'}
                              </td>
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {r['CodContAux1'] || '—'}
                              </td>
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {r['UsuarioManutencao'] || '—'}
                              </td>
                            </tr>
                          );
                        })}
                        {previewRows.length > 5 && (
                          <tr className="border-t bg-muted/10">
                            <td
                              colSpan={11}
                              className="px-3 py-2 text-center text-muted-foreground"
                            >
                              + {previewRows.length - 5} registros restantes...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2">
            <Button variant="outline" onClick={() => setImportState(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarImport}
              disabled={importMutation.isPending || (importState?.missingRequired?.length ?? 0) > 0}
            >
              {importMutation.isPending
                ? 'Importando...'
                : `Importar ${importState?.regras.length ?? 0} regras`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={regraParaExcluir != null}
        onOpenChange={open => !open && setRegraParaExcluir(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir regra</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove a regra selecionada. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={excluirMutation.isPending}
              onClick={() => setRegraParaExcluir(null)}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={excluirMutation.isPending}
            >
              {excluirMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {totalPaginas > 1 && (
        <Pagination
          currentPage={pagina}
          totalPages={totalPaginas}
          onPageChange={setPagina}
          itensPorPagina={itensPorPagina}
          onItensPorPaginaChange={handleItensPorPaginaChange}
        />
      )}
    </div>
  );
}
